import React, { useState, useEffect, useRef } from 'react';
import ProviderIcons from './ProviderIcons';
import TrailerEmbed from './TrailerEmbed';
import { useLocation } from 'react-router-dom';
import CastList from './CastList';
import SearchBar from './SearchBar';
import './HomePage.css'; // Ensure you have the correct CSS file for styling
import Cookies from 'js-cookie';
import { sortMediaList, getGenreNames, fetchDataForItems, hasVisibleMedia, getTmdbLink, handleDeleteItem } from './utils';

const HomePage = () => {
  // State for active panel toggles per item
  const [activePanel, setActivePanel] = useState({});
  const [visibleMedia, setVisibleMedia] = useState({ movies: [], tv: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [castData, setCastData] = useState({});
  const [videoData, setVideoData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  // New state for watchlist search matches
  const [watchlistMatches, setWatchlistMatches] = useState([]);
  // Track which item is being deleted for fade-out animation
  const [deletingItemId, setDeletingItemId] = useState(null);
  // Handles search and filters watchlist items for matches
  const [sortMode, setSortMode] = useState('alphabetical');

  const cycleSortMode = () => {
    setSortMode(prev =>
      prev === 'alphabetical' ? 'rating' :
        prev === 'rating' ? 'recent' :
          'alphabetical'
    );
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    const trimmedTerm = term.trim();
    if (trimmedTerm.length >= 3) {
      const storedMovies = JSON.parse(localStorage.getItem('sortedWatchList-movies')) || [];
      const storedTV = JSON.parse(localStorage.getItem('sortedWatchList-tv-shows')) || [];
      const allItems = [...storedMovies, ...storedTV];
      const matched = allItems.filter(item =>
        item.title?.toLowerCase().includes(trimmedTerm.toLowerCase())
      );
      setWatchlistMatches(matched);
    } else {
      setWatchlistMatches([]);
      setSearchTerm('');
    }
  };
  // Read section param from URL to set initial media type, with localStorage persistence
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const sectionParam = queryParams.get('section');
  const [activeMediaType, setActiveMediaType] = useState(() => {
    const stored = localStorage.getItem('activeMediaType');
    if (stored === 'Movies' || stored === 'TV') return stored;
    return sectionParam === 'movies' ? 'Movies' : 'TV';
  });

  // Store activeMediaType in localStorage when it changes
  useEffect(() => {
    localStorage.setItem('activeMediaType', activeMediaType);
  }, [activeMediaType]);
  // Compute counts for TV and Movies watchlists
  const tvCount = visibleMedia.tv.length;
  const movieCount = visibleMedia.movies.length;
  // State for tracking expanded trailer
  const [expandedVideoId, setExpandedVideoId] = useState(null);
  const username = Cookies.get("username") || 'default';
  document.title = 'Watch It Buddy';

  // Loads trailer videos for watchlist items
  const fetchVideos = async () => {
    const newVideoData = await fetchDataForItems(
      visibleMedia,
      '/api/videos',
      data => data.videos?.results || []
    );
    setVideoData(newVideoData);
  };

  // Trigger video fetch when media becomes available
  useEffect(() => {
    if (hasVisibleMedia(visibleMedia)) {
      fetchVideos();
    }
  }, [visibleMedia]);

  useEffect(() => {
    const storedMoviesList = localStorage.getItem('sortedWatchList-movies');
    const storedTVList = localStorage.getItem('sortedWatchList-tv-shows');

    const parsedMoviesList = storedMoviesList ? JSON.parse(storedMoviesList) : [];
    const parsedTVList = storedTVList ? JSON.parse(storedTVList) : [];

    setVisibleMedia({ movies: parsedMoviesList, tv: parsedTVList });

    setIsLoading(false);
  }, [username]);

  // Loads cast details for TV and movie items
  useEffect(() => {
    const fetchCasts = async () => {
      const newCastData = await fetchDataForItems(
        visibleMedia,
        '/api/cast',
        data => data.cast || []
      );
      setCastData(newCastData);
    };
    if (hasVisibleMedia(visibleMedia)) {
      fetchCasts();
    }
  }, [visibleMedia]);

  // Listen for watchlistUpdated event to refresh visibleMedia in real time
  useEffect(() => {
    const handleWatchlistUpdated = (e) => {
      console.log('>>>> Watchlist updated for media type:', e.detail.mediaType);
      const updatedMediaType = e.detail.mediaType;
      const storageKey = `sortedWatchList-${updatedMediaType}`;
      const updatedList = JSON.parse(localStorage.getItem(storageKey) || '[]');
      setVisibleMedia(prev => ({ ...prev, [updatedMediaType]: updatedList }));
    };

    window.addEventListener("watchlistUpdated", handleWatchlistUpdated);
    return () => window.removeEventListener("watchlistUpdated", handleWatchlistUpdated);
  }, []);

  // --- Providers state and fetching logic (moved outside renderQuickPicks) ---
  const [providers, setProviders] = useState({});
  useEffect(() => {
    const fetchProviders = async () => {
      console.log('Fetching providers for visibleMedia:', visibleMedia);
      const newProviderData = await fetchDataForItems(
        visibleMedia,
        '/w/providers?',
        data => data.providers || []
      );
      console.log('Fetched provider data:', newProviderData);
      setProviders(newProviderData);
    };
    if (hasVisibleMedia(visibleMedia)) {
      fetchProviders();
    }
  }, [visibleMedia]);

  // --- AI Quotes state ---
  const [quotes, setQuotes] = useState({});
  const [loadingQuotes, setLoadingQuotes] = useState({});
  // New state for quote visibility tracking per item
  const [quoteVisibility, setQuoteVisibility] = useState({});

  // Function to handle "View Quotes" button click with toggle visibility
  const handleViewQuoteClick = async (item, mediaType) => {
    const id = item.tmdb_id;
    // If already loading, skip
    if (loadingQuotes[id]) return;
    // Toggle visibility if quote is already fetched
    if (quotes[id]) {
      setQuoteVisibility(prev => ({ ...prev, [id]: !prev[id] }));
      return;
    }
    setLoadingQuotes(prev => ({ ...prev, [id]: true }));
    try {
      const response = await fetch('/api/gpt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: item.title || item.name,
          media_type: mediaType,
          mode: 'quotes',
        }),
      });
      const data = await response.json();
      if (data.response) {
        setQuotes(prev => ({ ...prev, [id]: data.response }));
        setQuoteVisibility(prev => ({ ...prev, [id]: true }));
      }
    } catch (error) {
      console.error('Failed to fetch quote:', error);
    } finally {
      setLoadingQuotes(prev => ({ ...prev, [id]: false }));
    }
  };
  const renderQuickPicks = (type) => {
    const finalList = watchlistMatches.length > 0
      ? visibleMedia[type].filter(item =>
        watchlistMatches.some(match => match.tmdb_id === item.tmdb_id)
      )
      : visibleMedia[type];
    const sortedList = sortMediaList(finalList, sortMode);
    const quickPickUrl = `/quick-pick?m=${type}`;
    const quickPickLabel = type === 'tv' ? '🎯 Quick Pick TV' : '🎬 Quick Pick Movies';
    return (
      <section className="homepage-section">
        <div style={{ textAlign: 'center', marginBottom: '0px' }}>
          <a href={quickPickUrl} className="quick-pick-button" style={{ fontSize: '18px', padding: '0px 20px' }}>
            {quickPickLabel}
          </a>
        </div>
        <div>
          {sortedList.map((item, index) => {
            // Default panel to overview if not set
            if (!activePanel[item.tmdb_id]) {
              setActivePanel(prev => ({ ...prev, [item.tmdb_id]: 'overview' }));
            }

            // --- Auto-fetch quotes and cast chat when toggled and not loaded ---
            const id = item.tmdb_id;
            const mediaType = type === 'tv' ? 'tv' : 'movie';

            // Auto-fetch quotes if quotes panel is active and not loaded yet
            if (activePanel[id] === 'quotes' && !quotes[id] && !loadingQuotes[id]) {
              handleViewQuoteClick(item, mediaType);
            }

            // Auto-fetch cast chat if chat panel is active and not loaded yet, only once per session per id
            if (
              activePanel[id] === 'chat' &&
              !quotes[`chat-${id}`] &&
              !loadingQuotes[`chat-${id}`] &&
              !window[`__chatFetched_${id}`]
            ) {
              window[`__chatFetched_${id}`] = true;
              setLoadingQuotes(prev => ({ ...prev, [`chat-${id}`]: true }));
              fetch('/api/gpt', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  title: item.title || item.name,
                  media_type: mediaType,
                  mode: 'chat',
                }),
              })
                .then(res => res.json())
                .then(data => {
                  if (data.response) {
                    setQuoteVisibility(prev => ({ ...prev, [`chat-${id}`]: true }));
                    // Store as array of lines (split on \n)
                    setQuotes(prev => ({ ...prev, [`chat-${id}`]: Array.isArray(data.response) ? data.response : data.response.split('\n') }));
                  }
                })
                .finally(() => {
                  setLoadingQuotes(prev => ({ ...prev, [`chat-${id}`]: false }));
                });
            }

            // Ensure TMDB link uses correct media type: 'tv' or 'movie' (singular)
            const tmdbLink = getTmdbLink(item, type === 'tv' ? 'tv' : 'movie');
            // Debug log for TMDB link, id, and type
            console.log("TMDB link debug:", item.title, item.tmdb_id, type, getTmdbLink(item, type === 'tv' ? 'tv' : 'movie'));
            const genreNames = getGenreNames(item, type);
            // Logging providers for troubleshooting
            console.log("Checking providers for", item.title, item.tmdb_id, providers[item.tmdb_id]);
            return (
              <div
                className={`list-item${deletingItemId === item.tmdb_id ? ' fade-out' : ''}`}
                key={index}
                style={{
                  backgroundImage: `url(https://image.tmdb.org/t/p/w1066_and_h600_bestv2/${item.backdrop_path || item.poster_path})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  marginBottom: '40px',
                  padding: '20px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '20px',
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  backgroundBlendMode: 'overlay',
                  position: 'relative',
                }}
              >
                {/* New top flex wrapper for toggle bar and delete button */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    width: '100%',
                    marginBottom: '10px'
                  }}
                >
                  <div style={{ float: 'left' }}>
                    <div className="media-toggle">
                      <button
                        className={`media-toggle-button ${activePanel[item.tmdb_id] === 'overview' ? 'active' : ''}`}
                        onClick={() => setActivePanel(prev => ({ ...prev, [item.tmdb_id]: 'overview' }))}
                      >
                        📖 Overview
                      </button>
                      <button
                        className={`media-toggle-button ${activePanel[item.tmdb_id] === 'cast' ? 'active' : ''}`}
                        onClick={() => setActivePanel(prev => ({ ...prev, [item.tmdb_id]: 'cast' }))}
                      >
                        🎭 Cast
                      </button>
                      <button
                        className={`media-toggle-button ${activePanel[item.tmdb_id] === 'trailer' ? 'active' : ''}`}
                        onClick={() => setActivePanel(prev => ({ ...prev, [item.tmdb_id]: 'trailer' }))}
                      >
                        🎬 Trailer
                      </button>
                      <button
                        className={`media-toggle-button ${activePanel[item.tmdb_id] === 'chat' ? 'active' : ''}`}
                        onClick={() => setActivePanel(prev => ({ ...prev, [item.tmdb_id]: 'chat' }))}
                      >
                        💬 Chat
                      </button>
                      <button
                        className={`media-toggle-button ${activePanel[item.tmdb_id] === 'quotes' ? 'active' : ''}`}
                        onClick={() => setActivePanel(prev => ({ ...prev, [item.tmdb_id]: 'quotes' }))}
                      >
                        📝 Quotes
                      </button>
                      <button
                        className={`media-toggle-button ${activePanel[item.tmdb_id] === 'reviews' ? 'active' : ''}`}
                        onClick={() => setActivePanel(prev => ({ ...prev, [item.tmdb_id]: 'reviews' }))}
                      >
                        🗣️ Reviews
                      </button>
                    </div>
                  </div>
                  <div style={{ float: 'right' }}>
                    <button
                      onClick={() =>
                        handleDeleteItem(item.tmdb_id, type, visibleMedia, setVisibleMedia, setDeletingItemId)
                      }
                      className="delete-button"
                      disabled={deletingItemId === item.tmdb_id}
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <a href={tmdbLink} target="_blank" rel="noreferrer">
                    <img
                      src={item.full_poster_path}
                      alt={item.title}
                      className="poster-image"
                    />
                  </a>
                  {/* Move View Episodes button here, below poster */}
                  {type === 'tv' && (
                    <a href={`https://www.themoviedb.org/tv/${item.tmdb_id}/seasons`} target="_blank" rel="noreferrer">
                      <button className="btn btn-secondary" style={{ marginTop: '10px' }}>
                        📺 View Episodes
                      </button>
                    </a>
                  )}
                </div>
                <div className="media-text-content">
                  {/* Overview tab content: title, year, genres, rating, and overview now below the toggle bar */}
                  {activePanel[item.tmdb_id] === 'overview' && (
                    <>
                      <a
                        href={tmdbLink}
                        target="_blank"
                        rel="noreferrer"
                        className="media-title-link"
                      >
                        {item.title}
                      </a>
                      <div className="media-subtext">
                        {(item.release_date || item.first_air_date || '').slice(0, 4)} | {genreNames}
                        {item.vote_average !== undefined && (
                          <span style={{ marginLeft: '10px' }}>⭐ {(item.vote_average / 2).toFixed(1)} / 5</span>
                        )}
                      </div>
                      <p className="media-overview">
                        {(item.overview || item.description || 'No description available.')}{' '}
                      </p>
                      {providers[item.tmdb_id]?.length > 0 && (
                        <div style={{
                          display: 'flex',
                          gap: '6px',
                          flexWrap: 'wrap',
                          justifyContent: 'flex-end',
                          maxWidth: '50%',
                        }}>
                          <ProviderIcons providers={providers[item.tmdb_id]} mediaType={type === 'tv' ? 'tv' : 'movie'} />
                        </div>
                      )}
                    </>
                  )}
                  {/* Only render CastList if "cast" toggle is active, now below the toggle bar */}
                  {activePanel[item.tmdb_id] === 'cast' && (
                    <div className="cast-grid">
                      {type === 'tv' ? (
                        <>
                          <div
                            className="cast-button-and-list"
                            style={{ display: 'flex', alignItems: 'flex-start' }}
                          >
                            <CastList cast={castData[item.tmdb_id]} tmdb_id={item.tmdb_id} />
                          </div>
                        </>
                      ) : (
                        <CastList cast={castData[item.tmdb_id]} tmdb_id={item.tmdb_id} />
                      )}
                    </div>
                  )}
                  {/* Conditional rendering for panels */}
                  {activePanel[item.tmdb_id] === 'trailer' && videoData[item.tmdb_id]?.length > 0 && (
                    <TrailerEmbed videoKey={videoData[item.tmdb_id][0].key} />
                  )}
                  {activePanel[item.tmdb_id] === 'quotes' && quoteVisibility[item.tmdb_id] && quotes[item.tmdb_id] && (
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
                      <div className="media-quotes" style={{ marginTop: '0px', backgroundColor: 'rgba(60,60,60,0.25)', borderRadius: '6px', width: 'fit-content', maxWidth: '90%' }}>
                        {quotes[item.tmdb_id]}
                      </div>
                    </div>
                  )}
                  {activePanel[item.tmdb_id] === 'chat' &&
                    quoteVisibility[`chat-${item.tmdb_id}`] &&
                    quotes[`chat-${item.tmdb_id}`] && (
                      <CastChatWithTyping lines={quotes[`chat-${item.tmdb_id}`]} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  };
  // Structure of the homepage: optional welcome panel, media toggles, and watchlist renderings
  return (
    <main className="homepage-main">
      
      {/* Media type toggle buttons and section buttons moved above welcome panel */}
      <div className="media-toggle-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0' }}>
        <div className="media-toggle">
          <button
            onClick={() => setActiveMediaType('TV')}
            className={`media-toggle-button ${activeMediaType === 'TV' ? 'active' : ''}`}
          >
            TV ({tvCount})
          </button>
          <button
            onClick={() => setActiveMediaType('Movies')}
            className={`media-toggle-button ${activeMediaType === 'Movies' ? 'active' : ''}`}
          >
            Movies ({movieCount})
          </button>
        </div>
        <div className="discover-group" style={{ display: 'flex', gap: '10px' }}>
          <button
            className="discover-btn active"
            disabled
            style={{
              backgroundColor: '#444',
              color: '#ccc',
              cursor: 'default'
            }}
          >
            Top Picks
          </button>

          {/* Watchlist button inserted between Quick Pick and Discover */}
          <a
            href={`/quick-pick-elimination?m=${activeMediaType === 'TV' ? 'tv-shows' : 'movies'}`}
            className="discover-btn"
          >
            Watch list
          </a>
        </div>
      </div>
      {/* Quick Pick TV/Movies images/buttons directly under toggles and welcome panel */}
      <section className="homepage-section" style={{ paddingTop: '0px' }}>
        <SearchBar
          showQuote={true}
          initialSearchTerm={searchTerm}
          onSearch={handleSearch}
          watchlistMatches={watchlistMatches}
        />
      </section>
      <div
        className="sort-toggle-container"
        style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 20px' }}
      >
        <div className="desktop-sort-options">
          <button className={`btn btn-sm ${sortMode === 'alphabetical' ? 'btn-primary' : 'btn-outline-light'}`} onClick={() => setSortMode('alphabetical')}>
            A → Z
          </button>
          <button className={`btn btn-sm ${sortMode === 'rating' ? 'btn-primary' : 'btn-outline-light'}`} onClick={() => setSortMode('rating')}>
            <i className="fas fa-quote-left" style={{ marginRight: '6px' }}></i> Rating
          </button>          
          <button className={`btn btn-sm ${sortMode === 'recent' ? 'btn-primary' : 'btn-outline-light'}`} onClick={() => setSortMode('recent')}>
            <i className="fas fa-clock" style={{ marginRight: '6px' }}></i> Recently Added
          </button>
        </div>
      </div>
      {/* Media type toggle bar moved above PromptStrip */}
      {/* Empty state message below toggle buttons */}
      {activeMediaType === 'TV' && visibleMedia['tv'].length === 0 && (
        <div style={{ textAlign: 'center', color: '#bbb', marginTop: '40px', fontSize: '1.1rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '10px' }}>😴</div>
          <p>Your TV watchlist is feeling a little empty.</p>
          <p>
            Try <a href="/quick-pick?m=tv" style={{ color: '#0d6efd', textDecoration: 'none' }}>Quick Pick</a> or use the <a href="/search" style={{ color: '#0d6efd', textDecoration: 'none' }}>Search</a> bar to add something new.
          </p>
        </div>
      )}
      {activeMediaType === 'Movies' && visibleMedia['movies'].length === 0 && (
        <div style={{ textAlign: 'center', color: '#bbb', marginTop: '40px', fontSize: '1.1rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🎬</div>
          <p>Your Movies watchlist is waiting for a star.</p>
          <p>
            Try <a href="/quick-pick?m=movies" style={{ color: '#0d6efd', textDecoration: 'none' }}>Quick Pick</a> or use the <a href="/search" style={{ color: '#0d6efd', textDecoration: 'none' }}>Search</a> bar to explore.
          </p>
        </div>
      )}
      {/* Section rendering based on activeMediaType */}
      {activeMediaType === 'TV' && renderQuickPicks('tv')}
      {activeMediaType === 'Movies' && renderQuickPicks('movies')}
    </main>
  );
};
// Removed unused handleQuickPickSelection and other removed code.
export default HomePage;
const CastChatWithTyping = ({ lines }) => {
  const [displayedMessages, setDisplayedMessages] = useState([]);
  const chatEndRef = useRef(null);
  // Ref to prevent re-triggering of animation logic
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    // Guard clause: only run animation logic once for given lines
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    console.log("CastChatWithTyping: lines received", lines);
    const parsed = lines
      .filter(line => typeof line === 'string' && line.includes(':'))
      .map(line => {
        const [name, message] = line.split(':');
        return { name: name.trim(), message: message.trim() };
      });

    setDisplayedMessages([]); // Clear initially
    let idx = 0;

    // Updated displayNext to ensure only one typing indicator and it's removed before showing the message
    const displayNext = () => {
      if (idx >= parsed.length) {
        return;
      }

      const current = parsed[idx];
      const pause = Math.max(1200, Math.min(3500, current.message.length * 60));

      // log before typing indicator
      console.log("Displaying typing for:", current);
      // Set typing indicator only, without pushing duplicates
      setDisplayedMessages(prev => [
        ...prev.filter(msg => msg.message !== '__TYPING__'),
        { name: current.name, message: '__TYPING__' }
      ]);

      setTimeout(() => {
        // log before displaying message
        console.log("Displaying message:", current);
        setDisplayedMessages(prev => {
          // Remove last typing message and replace with actual message
          const withoutTyping = prev.filter(msg => msg.message !== '__TYPING__');
          const updated = [...withoutTyping, current];
          // log after updating displayedMessages
          console.log("Updated displayedMessages:", updated);
          return updated;
        });
        idx++;
        setTimeout(displayNext, 1000); // Additional pause after message displays
      }, pause);
    };

    setTimeout(displayNext, 100); // Give shimmer a frame to render
  }, [lines]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollTop = chatEndRef.current.scrollHeight;
    }
  }, [displayedMessages]);

  return (
    <>
      {displayedMessages.length === 0 && (
        <div
          className="cast-chat-loading"
          style={{ color: '#ccc', fontStyle: 'italic', padding: '10px' }}
        >
          Loading...
        </div>
      )}
      <div
        className="imessage-chat-container"
        ref={chatEndRef}
        style={{ minHeight: '250px', maxHeight: '250px', overflowY: 'auto' }}
      >
        <div className="imessage-chat-group">
          {displayedMessages.map((msg, idx) => (
            <div key={idx} className="imessage-chat-sender">
              <div className="imessage-chat-name">{msg.name}</div>
              <div className="imessage-chat-bubble">
                {msg.message === '__TYPING__' ? (
                  <span className="typing-indicator">
                    <span>.</span>
                    <span>.</span>
                    <span>.</span>
                  </span>
                ) : (
                  msg.message
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <style>
        {`
        .cast-chat-loading {
          padding: 10px;
          margin-bottom: 10px;
        }
        `}
      </style>
    </>
  );
};